import React, { useRef, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGithub,
  faLinkedin,
  faInstagram,
  faYoutube,
  faWhatsapp
} from "@fortawesome/free-brands-svg-icons";
import {
  Box,
  HStack,
  Text,
  Flex,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  VStack,
  Container,
  Grid,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import "./styles.css";

// Social links
const socials = [
  {
    name: "GitHub",
    url: "https://github.com/TheIronA",
    icon: faGithub,
  },
  {
    name: "LinkedIn",
    url: "https://www.linkedin.com/in/alwi-al-haddad/",
    icon: faLinkedin,
  },
  {
    name: "Instagram",
    url: "https://instagram.com/mr.alwi",
    icon: faInstagram,
  },
  {
    name: "YouTube",
    url: "https://youtube.com/@alwialhaddad",
    icon: faYoutube,
  },
  {
    name: "WhatsApp",
    url: "https://wa.me/601170171030",
    icon: faWhatsapp,
  }
];

const Header = () => {
  const headerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isContactSectionVisible, setIsContactSectionVisible] = useState(false);
  const [scrolledDown, setScrolledDown] = useState(false);

  useEffect(() => {
    let prevScrollPos = window.scrollY;
    const headerElement = headerRef.current;
    
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      if (!headerElement) return;

      // Update scrolled state for visual changes
      setScrolledDown(currentScrollPos > 50);

      if (!isMobile) { 
        if (prevScrollPos > currentScrollPos || currentScrollPos < 10) {
          headerElement.style.transform = "translateY(0)";
        } else {
          headerElement.style.transform = "translateY(-200px)";
        }
      } else {
        headerElement.style.transform = "translateY(0)";
      }
      
      prevScrollPos = currentScrollPos;

      if (location.pathname === '/') {
        const contactSection = document.getElementById('contactme-section');
        if (contactSection) {
          const sectionTop = contactSection.getBoundingClientRect().top;
          const viewportHeight = window.innerHeight;
          setIsContactSectionVisible(sectionTop < viewportHeight * 0.75);
        } else {
          setIsContactSectionVisible(false);
        }
      } else {
         setIsContactSectionVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); 

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        onClose();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onClose]);

  const smoothScrollTo = (id, behavior = 'smooth') => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: behavior,
      });
    }
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollBehavior: 'instant' } });
    }
    onClose();
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToSection: 'contactme-section', scrollBehavior: 'instant' } });
    } else {
      smoothScrollTo("contactme-section", 'instant');
    }
    onClose();
  };

  const navLinks = [
    { name: "Home", path: "/", onClick: handleHomeClick },
    { name: "Projects", path: "/projects", onClick: onClose },
    { name: "My Blog", path: "/blog", onClick: onClose },
    { name: "Contact", path: "/", onClick: handleContactClick },
  ];

  return (
    <Box
      ref={headerRef}
      as="header"
      position="fixed"
      w="100%"
      zIndex="1000"
      transition="all 0.3s ease-in-out, transform 0.3s"
      backdropFilter={scrolledDown ? "blur(10px)" : "none"}
      bg={scrolledDown ? "rgba(26, 32, 44, 0.85)" : "brand.bg.dark"}
      boxShadow={scrolledDown ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "0 1px 0 rgba(255, 255, 255, 0.1)"}
      py={scrolledDown ? 3 : 4}
    >
      <Container maxW="1200px">
        <Flex
          align="center"
          justify="space-between"
          w="100%"
          h="60px"
          position="relative"
        >
          {/* Left: Logo/Brand */}
          <Link to="/" onClick={handleHomeClick}>
            <Text
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight="bold"
              letterSpacing="tight"
              color="brand.text.primary"
              position="relative"
              _after={{
                content: "''",
                position: "absolute",
                left: "0",
                bottom: "-2px",
                width: "0",
                height: "2px",
                bg: "brand.secondary",
                transition: "width 0.3s ease"
              }}
              _hover={{
                color: "brand.secondary",
                _after: {
                  width: "100%"
                }
              }}
            >
              AH.
            </Text>
          </Link>

          {/* Center: Navigation Links - Only visible on desktop */}
          {!isMobile && (
            <HStack
              as="nav"
              spacing={8}
              display={{ base: "none", md: "flex" }}
              position="absolute"
              left="50%"
              transform="translateX(-50%)"
            >
              {navLinks.map((link, index) => {
                let linkIsActive = false;
                if (link.name === "Home") {
                  linkIsActive = location.pathname === '/' && !isContactSectionVisible;
                } else if (link.name === "Contact") {
                  linkIsActive = location.pathname === '/' && isContactSectionVisible;
                } else {
                  linkIsActive = location.pathname.startsWith(link.path) && link.path !== '/';
                }

                return (
                  <Link 
                    key={index} 
                    to={link.path}
                    onClick={(e) => {
                      if (link.onClick) {
                        link.onClick(e);
                      } else {
                        navigate(link.path);
                        onClose();
                      }
                    }}
                  >
                    <Text
                      fontSize="md"
                      fontWeight="500"
                      color={linkIsActive ? "brand.secondary" : "brand.text.primary"}
                      position="relative"
                      _after={{
                        content: "''",
                        position: "absolute",
                        left: linkIsActive ? "0" : "50%",
                        bottom: "-2px",
                        width: linkIsActive ? "100%" : "0",
                        height: "2px",
                        bg: "brand.secondary",
                        transition: "all 0.3s ease",
                        transform: linkIsActive ? "none" : "translateX(-50%)"
                      }}
                      _hover={{
                        color: "brand.secondary",
                        _after: {
                          width: "100%",
                          left: "0",
                          transform: "none"
                        }
                      }}
                    >
                      {link.name}
                    </Text>
                  </Link>
                );
              })}
            </HStack>
          )}

          {/* Right: Social Icons & Menu Button */}
          <HStack spacing={4}>
            {/* Social Icons - Only visible on desktop */}
            {!isMobile && (
              <HStack spacing={3}>
                {socials.map((social, index) => (
                  <IconButton
                    key={index}
                    as="a"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                    icon={<FontAwesomeIcon icon={social.icon} />}
                    variant="ghost"
                    color="white"
                    size="md"
                    fontSize="lg"
                    borderRadius="full"
                    _hover={{
                      color: "white",
                      transform: "translateY(-2px)",
                      bg: "rgba(255, 255, 255, 0.1)"
                    }}
                  />
                ))}
              </HStack>
            )}

            {/* Hamburger Menu - Only visible on mobile */}
            {isMobile && (
              <IconButton
                aria-label={isOpen ? "Close menu" : "Open menu"}
                icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
                variant="ghost"
                color="brand.text.primary"
                onClick={onOpen}
                _hover={{
                  bg: "whiteAlpha.200"
                }}
              />
            )}
          </HStack>
        </Flex>
      </Container>

      {/* Mobile Menu Drawer */}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={headerRef}
      >
        <DrawerOverlay bg="rgba(0, 0, 0, 0.7)" backdropFilter="blur(5px)" />
        <DrawerContent bg="brand.primary" maxW="300px">
          <DrawerCloseButton color="brand.text.primary" size="lg" mt={2} />
          <DrawerBody pt={10}>
            <VStack spacing={6} align="flex-start" w="100%">
              {navLinks.map((link, index) => {
                let linkIsActive = false;
                if (link.name === "Home") {
                  linkIsActive = location.pathname === '/' && !isContactSectionVisible;
                } else if (link.name === "Contact") {
                  linkIsActive = location.pathname === '/' && isContactSectionVisible;
                } else {
                  linkIsActive = location.pathname.startsWith(link.path) && link.path !== '/';
                }

                return (
                  <Link
                    key={index}
                    to={link.path}
                    onClick={(e) => {
                      if (link.onClick) {
                        link.onClick(e);
                      } else {
                        navigate(link.path);
                        onClose();
                      }
                    }}
                    style={{ width: "100%" }}
                  >
                    <Text
                      fontSize="xl"
                      fontWeight="500"
                      py={2}
                      color={linkIsActive ? "brand.secondary" : "brand.text.primary"}
                      borderBottom="1px solid"
                      borderColor="whiteAlpha.300"
                      w="100%"
                      _hover={{
                        color: "brand.secondary",
                        pl: 2,
                        transition: "all 0.2s ease"
                      }}
                    >
                      {link.name}
                    </Text>
                  </Link>
                );
              })}

              {/* Social Icons in Mobile Menu */}
              <Box pt={4} w="100%">
                <Grid templateColumns="repeat(3, 1fr)" gap={4} w="100%">
                  {socials.map((social, index) => (
                    <IconButton
                      key={index}
                      as="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                      icon={<FontAwesomeIcon icon={social.icon} />}
                      variant="ghost"
                      color="white"
                      fontSize="22px"
                      size="lg"
                      _hover={{
                        color: "white",
                        bg: "whiteAlpha.200"
                      }}
                    />
                  ))}
                </Grid>
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;
